import React, { createContext, useState, useEffect, useCallback, useContext, useMemo, ReactNode } from 'react';
import axios, { AxiosInstance } from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export type UserRole = 'dealer' | 'admin' | 'employee' | 'cnf_agent';

export interface AuthUser {
  id: string;
  mobile?: string;
  email?: string;
  full_name?: string;
  role?: UserRole;
  profile_complete?: boolean;
  kyc_status?: 'pending' | 'approved' | 'rejected' | 'submitted';
  dealer_code?: string | null;
  dealer_type?: 'PD' | 'LD';
  is_test_account?: boolean;
  [key: string]: unknown;
}

export interface AuthContextValue {
  user: AuthUser | null;
  setUser: (u: AuthUser) => void;
  api: AxiosInstance;
  login: (tokenOrCsrf: string, userData: AuthUser, csrfToken?: string) => void;
  loginWithToken: (tokenOrCsrf: string, userData: AuthUser, csrfToken?: string) => void;
  logout: () => Promise<void>;
  loading: boolean;
  /**
   * Boolean-ish flag for legacy `if (token)` guards. The actual JWT lives in an
   * httpOnly cookie and is never exposed to JS.
   */
  token: 'cookie' | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// SECURITY: JWT is kept ONLY in an httpOnly cookie set by the backend on login.
// We never read or store the JWT in JS-accessible storage. localStorage contains only:
//   - `csrf_token`: the double-submit CSRF token (low-sensitivity — XSS would bypass this anyway)
//   - `auth_user`: cached user profile for instant paint (not trusted, re-verified on mount via /auth/me)
const CSRF_KEY = 'csrf_token';
const USER_KEY = 'auth_user';

const getCsrf = (): string | null => localStorage.getItem(CSRF_KEY);
const setCsrf = (t: string): void => { if (t) localStorage.setItem(CSRF_KEY, t); };
const clearCsrf = (): void => localStorage.removeItem(CSRF_KEY);

const getStoredUser = (): AuthUser | null => {
  try { const raw = localStorage.getItem(USER_KEY); return raw ? JSON.parse(raw) as AuthUser : null; } catch { return null; }
};
const setStoredUser = (user: AuthUser): void => localStorage.setItem(USER_KEY, JSON.stringify(user));
const clearStoredUser = (): void => localStorage.removeItem(USER_KEY);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);
  const [loading, setLoading] = useState<boolean>(true);

  const api: AxiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API}/api`,
      withCredentials: true, // send httpOnly auth cookie
    });
    instance.interceptors.request.use((cfg) => {
      const csrf = getCsrf();
      if (csrf && !['get', 'head', 'options'].includes((cfg.method || 'get').toLowerCase())) {
        cfg.headers.set('X-CSRF-Token', csrf);
      }
      return cfg;
    });
    return instance;
  }, []);

  useEffect(() => {
    // Only verify dealer session when on a dealer route. Admin & employee routes have
    // their own /admin/me and /employee/me checks in their layouts, and calling
    // /auth/me for admin users returns 404 ("admin" isn't in the users collection),
    // which would spuriously wipe the shared csrf_token from localStorage and break
    // admin/employee mutations.
    const path = window.location.pathname || '';
    if (path.startsWith('/admin') || path.startsWith('/employee')) {
      setLoading(false);
      return;
    }
    api.get('/auth/me')
      .then((res) => {
        const userData = (res.data.user || res.data) as AuthUser;
        setUser(userData);
        setStoredUser(userData);
      })
      .catch((err) => {
        // Only treat explicit unauthorized as "not logged in". 404/500 may just mean
        // the dealer endpoint doesn't recognize this session (e.g. admin signed in).
        if (err.response?.status === 401) {
          clearCsrf();
          clearStoredUser();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, [api]);

  const login = useCallback((tokenOrCsrf: string, userData: AuthUser, csrfToken?: string) => {
    // tokenOrCsrf is legacy positional arg (old callsites passed JWT there). We now ignore
    // the JWT (it's in the cookie) and only persist the CSRF token (3rd arg) or, as
    // a fallback, the first arg when called with csrf-only sites.
    const csrf = csrfToken || tokenOrCsrf;
    if (csrf && typeof csrf === 'string') setCsrf(csrf);
    setUser(userData);
    setStoredUser(userData);
  }, []);

  const loginWithToken = login;

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* cookie may already be gone */ }
    clearCsrf();
    clearStoredUser();
    setUser(null);
  }, [api]);

  const updateUser = useCallback((updatedUser: AuthUser) => {
    setUser(updatedUser);
    setStoredUser(updatedUser);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user, setUser: updateUser, api, login, loginWithToken, logout, loading,
    // `token` is kept as a boolean-ish for legacy guards (`if (token)` checks).
    // Actual JWT is never exposed to JS.
    token: user ? 'cookie' : null,
  }), [user, updateUser, api, login, loginWithToken, logout, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
